import { useMemo } from "react";
import { cableEndpoints, type Cable, type CableEndpoint } from "../domain/patch";
import { serializeGraphicTreeToSvg } from "../synth-designer/adapters/svg";
import { unionRects } from "../synth-designer/geometry";
import type {
  GraphicEllipse,
  GraphicGroup,
  GraphicPath,
  GraphicRect,
  GraphicStroke,
  GraphicText,
  GraphicTree,
} from "../synth-designer/graphic-tree";
import { routeCable, type CableAnchor } from "../workspace/cable-routing";
import {
  compileInstrumentForPrint,
  type CompiledInstrumentRuntime,
} from "../workspace/compiled-instrument";

type WireframePanelProps = {
  runtime: CompiledInstrumentRuntime;
  controlValues: Record<string, number>;
  cables: Cable[];
  palette: "grayscale" | "monochrome";
};

const PRINT_INK = "#111111";
const PRINT_PAPER = "#ffffff";
const cablePatterns = [undefined, [22, 8], [5, 6], [16, 5, 3, 5], [2, 5]] as const;

function stroke(width: number, dash?: readonly number[]): GraphicStroke {
  return {
    colour: PRINT_INK,
    width,
    dash: dash ? [...dash] : undefined,
    cap: "round",
    join: "round",
  };
}

function circle(
  id: string,
  className: string,
  x: number,
  y: number,
  radius: number,
  width: number,
): GraphicEllipse {
  return {
    type: "ellipse",
    id,
    className,
    cx: x,
    cy: y,
    radiusX: radius,
    radiusY: radius,
    fill: { kind: "solid", colour: PRINT_PAPER },
    stroke: stroke(width),
  };
}

function text(
  id: string,
  className: string,
  value: string,
  x: number,
  y: number,
  fontSize: number,
): GraphicText {
  return {
    type: "text",
    id,
    className,
    value,
    x,
    y,
    align: "center",
    verticalAlign: "middle",
    frameMode: "hug",
    overflow: "visible",
    colour: PRINT_INK,
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize,
    fontWeight: 700,
  };
}

function looseEndLabel(
  cableId: string,
  endpoint: Extract<CableEndpoint, { kind: "loose" }>,
  index: number,
) {
  const value = (endpoint.label ?? "LOOSE END").toUpperCase();
  const width = Math.max(70, value.length * 7 + 12);
  const rect: GraphicRect = {
    type: "rect",
    id: `${cableId}:loose-label:${index}:box`,
    className: "wire-loose-label-box",
    x: endpoint.x + 10,
    y: endpoint.y - 24,
    width,
    height: 20,
    fill: { kind: "solid", colour: PRINT_PAPER },
    stroke: stroke(1),
  };
  return [
    rect,
    text(
      `${cableId}:loose-label:${index}:text`,
      "wire-loose-label-text",
      value,
      endpoint.x + 10 + width / 2,
      endpoint.y - 14,
      11,
    ),
  ];
}

function cableGraphic(
  cable: Cable,
  anchorsByPort: ReadonlyMap<string, CableAnchor>,
): GraphicGroup | null {
  const endpoints = cableEndpoints(cable);
  const resolveEndpoint = (endpoint: CableEndpoint): CableAnchor | null =>
    endpoint.kind === "loose"
      ? { x: endpoint.x, y: endpoint.y, surface: "loose" }
      : (anchorsByPort.get(endpoint.portId) ?? null);
  const anchors = endpoints.map(resolveEndpoint);
  if (anchors.some((anchor) => !anchor)) return null;
  const resolvedAnchors = anchors as CableAnchor[];
  const junction =
    cable.branches?.length && cable.junction
      ? ({ ...cable.junction, surface: "loose" } satisfies CableAnchor)
      : null;
  const routes = junction
    ? resolvedAnchors.map((anchor, index) => routeCable(junction, anchor, cable.number + index))
    : [routeCable(resolvedAnchors[0]!, resolvedAnchors[1]!, cable.number)];
  const labelPoint = junction ?? routes[0]!.labelPoint;
  const dash = cable.prepared
    ? [18, 5, 2, 5]
    : cablePatterns[(cable.number - 1) % cablePatterns.length];
  const paths: GraphicPath[] = routes.map((route, index) => ({
    type: "path",
    id: `${cable.id}:route:${index}`,
    className: "wire-cable-path",
    data: route.path,
    stroke: stroke(5, dash),
  }));
  const endpointGraphics = endpoints.flatMap((endpoint, index) => {
    const anchor = resolvedAnchors[index]!;
    const stackable = endpoint.kind === "port" && endpoint.plugStyle === "stackable";
    return [
      circle(
        `${cable.id}:endpoint:${index}`,
        stackable ? "wire-stackable-plug" : "wire-cable-plug",
        anchor.x,
        anchor.y,
        stackable ? 11 : 8,
        stackable ? 3 : 4,
      ),
      ...(stackable
        ? [
            circle(
              `${cable.id}:endpoint:${index}:socket`,
              "wire-stackable-socket",
              anchor.x,
              anchor.y,
              5,
              2,
            ),
          ]
        : []),
      ...(endpoint.kind === "loose" ? looseEndLabel(cable.id, endpoint, index) : []),
    ];
  });
  const numberGraphics = [
    circle(`${cable.id}:number:disc`, "wire-cable-number", labelPoint.x, labelPoint.y, 11, 2),
    text(
      `${cable.id}:number:text`,
      "wire-cable-number-text",
      String(cable.number),
      labelPoint.x,
      labelPoint.y,
      12,
    ),
    ...(cable.prepared
      ? [
          text(
            `${cable.id}:prepared`,
            "wire-prepared-marker",
            "P",
            labelPoint.x + 18,
            labelPoint.y - 8,
            9,
          ),
        ]
      : []),
  ];

  return {
    type: "group",
    id: `print-cable:${cable.id}`,
    className: `wire-cable${cable.prepared ? " is-prepared" : ""}${junction ? " is-branched" : ""}`,
    children: [
      ...paths,
      ...endpointGraphics,
      ...(junction
        ? [circle(`${cable.id}:junction`, "wire-cable-junction", junction.x, junction.y, 13, 4)]
        : []),
      ...numberGraphics,
    ],
  };
}

function buildPrintTree(
  runtime: CompiledInstrumentRuntime,
  controlValues: Readonly<Record<string, number>>,
  cables: readonly Cable[],
) {
  const compiled = compileInstrumentForPrint(runtime, controlValues);
  const anchorsByPort = new Map(
    runtime.definition.ports.map((port) => [
      port.id,
      {
        ...port.position,
        surface: port.location === "rear" ? "rear" : "front",
      } satisfies CableAnchor,
    ]),
  );
  const cableGraphics = cables
    .map((cable) => cableGraphic(cable, anchorsByPort))
    .filter((graphic): graphic is GraphicGroup => graphic !== null);
  const cableLayer: GraphicGroup = {
    type: "group",
    id: "print-cables",
    className: "wire-cables",
    transform: {
      a: 1 / runtime.scale,
      b: 0,
      c: 0,
      d: 1 / runtime.scale,
      tx: 0,
      ty: 0,
    },
    children: cableGraphics,
  };
  const tree: GraphicTree = {
    type: "group",
    id: "print-scene",
    children: [compiled.renderPlan, cableLayer],
  };
  const bounds = unionRects(
    [...compiled.scene.artboards.values()]
      .filter(({ source }) => source.visible)
      .map(({ bounds: artboardBounds }) => artboardBounds),
  ) ?? { x: 0, y: 0, width: 100, height: 100 };
  return { bounds, tree };
}

export function WireframePanel({ runtime, controlValues, cables, palette }: WireframePanelProps) {
  const svg = useMemo(() => {
    const { bounds, tree } = buildPrintTree(runtime, controlValues, cables);
    return serializeGraphicTreeToSvg(tree, {
      width: bounds.width,
      height: bounds.height,
      viewBox: bounds,
      unit: "mm",
      title: `${runtime.definition.displayName} wireframe patch diagram`,
      description: "Compiled monochrome synth layout with numbered patch cables.",
      idPrefix: "print",
      rootAttributes: {
        class: `wireframe-panel palette-${palette}`,
        role: "img",
        "aria-label": `${runtime.definition.displayName} wireframe patch diagram`,
        "data-print-wireframe": "true",
      },
    });
  }, [cables, controlValues, palette, runtime]);

  return <div className="compiled-wireframe-host" dangerouslySetInnerHTML={{ __html: svg }} />;
}
